import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Eye, ExternalLink, Printer, ReceiptText } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { orgTreeApi } from '@/services/org-tree'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency } from '@/lib/utils'
import type { StoreOrderDetail, StoreOrderPayment } from '@/types'

type CompanyProfile = {
  name: string
  branchName: string
  logoUrl: string
  registrationDetails: string
  email: string
  phone: string
  address: string
  primaryColor: string
}

type BookingInvoicePanelProps = {
  order: StoreOrderDetail
  payments: StoreOrderPayment[]
  currency?: string | null
  serviceInstanceId?: string | null
  linkedJob?: {
    id: string | number
    jobRef?: string | null
    serviceInstanceId?: string | number | null
  } | null
  formatPaymentStatus: (status?: string | number | null) => string
  formatPaymentChannelLabel: (value?: string | number | null) => string
  getPaymentDisplayReference: (payment: StoreOrderPayment) => string
  isSuccessfulPaymentStatus: (status?: string | number | null) => boolean
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const compactText = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => String(part ?? '').trim()).filter(Boolean).join(', ')

const readString = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return ''
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const normalizeThemeColor = (value: string) => {
  const trimmed = value.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`
  return '#0f172a'
}

const parseAmount = (amount?: string | number | null) => {
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? amount : 0
  }
  if (typeof amount === 'string') {
    const parsed = Number(amount.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatMoney = (amount: number, currency?: string | null) => {
  const marker = String(currency ?? '').trim()
  if (/^[A-Za-z]{3}$/.test(marker)) {
    return formatCurrency(amount, marker.toUpperCase())
  }
  if (marker) {
    const sign = amount < 0 ? '-' : ''
    const formatted = Math.abs(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${sign}${marker}${formatted}`
  }
  return formatCurrency(amount)
}

const lightenHexColor = (hex: string, amount = 0.82) => {
  const normalized = normalizeThemeColor(hex).replace('#', '')
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount)
  return `#${[mix(r), mix(g), mix(b)].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

const renderCompanyLogoHtml = (logoUrl: string, name: string) => (
  logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(name)} logo" />`
    : `<div class="logo-fallback">${escapeHtml(name.slice(0, 2).toUpperCase())}</div>`
)

const buildCompanyProfile = (
  serviceInstance: unknown,
  fallbackName: string,
  parentServiceInstance?: unknown,
): CompanyProfile => {
  const config = serviceInstance && typeof serviceInstance === 'object'
    ? (serviceInstance as Record<string, unknown>).config
    : null
  const parentConfig = parentServiceInstance && typeof parentServiceInstance === 'object'
    ? (parentServiceInstance as Record<string, unknown>).config
    : null
  const configRecord = config && typeof config === 'object' ? config : null
  const parentConfigRecord = parentConfig && typeof parentConfig === 'object' ? parentConfig : null
  const source = serviceInstance && typeof serviceInstance === 'object' ? serviceInstance : {}
  const parentSource = parentServiceInstance && typeof parentServiceInstance === 'object' ? parentServiceInstance : {}

  const name = readString(configRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(source, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || readString(parentConfigRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(parentSource, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || fallbackName

  const logoUrl = readString(configRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(source, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
    || readString(parentConfigRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(parentSource, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])

  const registrationBody = readString(parentConfigRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(parentSource, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(configRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(source, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])

  const registrationName = readString(parentConfigRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(parentSource, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(configRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(source, ['company_registration_name', 'registered_company_name', 'legal_name'])

  const registrationNumber = readString(parentConfigRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(parentSource, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
    || readString(configRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(source, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])

  const registrationDetails = [
    registrationBody,
    registrationName && registrationName !== name ? registrationName : '',
    registrationNumber,
  ].filter(Boolean).join(' · ')

  const email = readString(configRecord, ['invoice_email', 'branch_email', 'contact_email', 'email'])
    || readString(source, ['email', 'branch_email', 'admin_email', 'branch_admin_email', 'primary_role_email', 'owner_email'])
    || readString(parentSource, ['email', 'admin_email', 'owner_email'])

  const phone = readString(configRecord, ['invoice_phone', 'branch_phone', 'contact_phone', 'phone', 'phn_nbr'])
    || readString(source, ['phn_nbr', 'phone', 'branch_phone', 'admin_phone', 'branch_admin_phone', 'primary_role_phone', 'owner_phone'])
    || readString(parentSource, ['phn_nbr', 'phone', 'admin_phone', 'owner_phone'])

  const address = readString(configRecord, ['invoice_address', 'company_address', 'address'])
    || compactText(
      readString(source, ['address_line_1', 'location']),
      readString(source, ['address_line_2']),
      readString(source, ['city']),
      readString(source, ['postcode', 'zipcode']),
      readString(source, ['country_name']),
    )

  const primaryColor = normalizeThemeColor(
    readString(configRecord, ['primary_color', 'brand_color', 'theme_color', 'invoice_primary_color'])
      || readString(source, ['primary_color', 'brand_color', 'theme_color'])
      || '#0f172a',
  )

  const branchName = readString(source, ['name', 'title', 'branch_name', 'service_instance_name'])

  return {
    name,
    branchName: branchName && branchName !== name ? branchName : '',
    logoUrl,
    registrationDetails,
    email,
    phone,
    address,
    primaryColor,
  }
}

const formatInvoiceDate = (value?: string | null) => {
  const raw = String(value ?? '').trim()
  if (!raw) return '-'
  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})/)
  if (match) {
    const [, day, month, year] = match
    return `${day}/${month}/${year}`
  }
  const parsed = new Date(raw.includes(' ') ? raw.replace(' ', 'T') : raw)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function BookingInvoicePanel({
  order,
  payments,
  currency,
  serviceInstanceId,
  linkedJob,
  formatPaymentStatus,
  formatPaymentChannelLabel,
  getPaymentDisplayReference,
  isSuccessfulPaymentStatus,
}: BookingInvoicePanelProps) {
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const resolvedServiceInstanceId = String(
    serviceInstanceId
    || order.service_instance_id
    || order.service_instance
    || '',
  ).trim()

  const serviceInstanceQuery = useQuery({
    queryKey: ['booking-invoice-service-instance', resolvedServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(resolvedServiceInstanceId),
    enabled: Boolean(resolvedServiceInstanceId),
  })
  const parentServiceInstanceId = serviceInstanceQuery.data?.parent_instance_id
  const parentServiceInstanceQuery = useQuery({
    queryKey: ['booking-invoice-parent-service-instance', parentServiceInstanceId, resolvedServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId && parentServiceInstanceId !== resolvedServiceInstanceId),
  })

  const companyProfile = useMemo(() => {
    const fallback = String(order.service_instance_name || 'Ellan One').trim() || 'Ellan One'
    return buildCompanyProfile(serviceInstanceQuery.data, fallback, parentServiceInstanceQuery.data)
  }, [order.service_instance_name, parentServiceInstanceQuery.data, serviceInstanceQuery.data])

  const invoiceHtml = useMemo(() => {
    const tableHeaderColor = lightenHexColor(companyProfile.primaryColor)
    const tableHeaderBorderColor = lightenHexColor(companyProfile.primaryColor, 0.68)
    const companyContact = compactText(companyProfile.email, companyProfile.phone)
    const invoiceNumber = `BK-${order.reference_id || order.id || 'invoice'}`
    const customerName = order.full_name || order.customer_name || 'Customer'
    const customerPhone = order.mobile || order.customer_phone || ''
    const customerEmail = order.email
      || String((order.user_data as { email?: string } | null | undefined)?.email ?? '').trim()
      || order.customer_email
      || ''
    const delivery = order.delivery_address_data
    const customerAddress = delivery
      ? compactText(
        delivery.door_no as string | undefined,
        delivery.building_name as string | undefined,
        delivery.street_line as string | undefined,
        (delivery.country_data as { name?: string } | undefined)?.name,
        delivery.zipcode as string | undefined,
      )
      : ''

    const particularRows = (order.order_items ?? []).map((item, index) => {
      const description = [item.product_variant_name, ...(item.addon_names ?? [])].filter(Boolean).join(' • ') || '-'
      const qty = Number(item.qty ?? 1) || 1
      const unit = parseAmount(item.unit_price)
      const amount = parseAmount(item.final_price) || unit * qty
      return {
        index: index + 1,
        item: item.product_name || `Item ${index + 1}`,
        description,
        quantity: String(qty),
        rate: formatMoney(unit, currency),
        amount: formatMoney(amount, currency),
        amountValue: amount,
      }
    })

    const lineSubtotal = particularRows.reduce((sum, row) => sum + row.amountValue, 0)
    const totalAmount = parseAmount(order.final_total ?? order.totals?.at(-1)?.amount ?? order.total) || lineSubtotal
    const paidAmount = payments
      .filter((payment) => isSuccessfulPaymentStatus(payment.payment_status))
      .reduce((sum, payment) => sum + parseAmount(payment.amount), 0)
    const balanceDue = Math.max(totalAmount - paidAmount, 0)

    const totalRows = (order.totals ?? []).map((line) => ({
      label: line.label,
      amount: formatMoney(parseAmount(line.amount), line.currency ?? currency),
    }))

    const renderedParticulars = particularRows.length > 0
      ? particularRows.map((row) => `
          <tr>
            <td class="center narrow">${row.index}</td>
            <td><strong>${escapeHtml(row.item)}</strong></td>
            <td>${escapeHtml(row.description)}</td>
            <td class="right">${escapeHtml(row.quantity)}</td>
            <td class="right">${escapeHtml(row.rate)}</td>
            <td class="right">${escapeHtml(row.amount)}</td>
          </tr>
        `).join('')
      : `
          <tr>
            <td class="center narrow">1</td>
            <td><strong>Booking</strong></td>
            <td>Booking #${escapeHtml(String(order.reference_id || order.id || ''))}</td>
            <td class="right">1</td>
            <td class="right">${escapeHtml(formatMoney(totalAmount, currency))}</td>
            <td class="right">${escapeHtml(formatMoney(totalAmount, currency))}</td>
          </tr>
        `

    const renderedPayments = payments.map((payment) => {
      const method = [
        formatPaymentChannelLabel(payment.payment_service),
        formatPaymentChannelLabel(payment.payment_mode),
        formatPaymentChannelLabel(payment.payment_method),
      ].filter(Boolean).join(' • ') || '-'
      return `
        <tr>
          <td>${escapeHtml(formatInvoiceDate(payment.created_on))}</td>
          <td>${escapeHtml(method)}</td>
          <td>${escapeHtml(getPaymentDisplayReference(payment))}</td>
          <td>${escapeHtml(formatPaymentStatus(payment.payment_status))}</td>
          <td class="right">${escapeHtml(formatMoney(parseAmount(payment.amount), currency ?? payment.currency))}</td>
        </tr>
      `
    }).join('')

    const renderedTotals = (totalRows.length > 0
      ? totalRows
      : [{ label: 'Total', amount: formatMoney(totalAmount, currency) }]
    ).map((row, index, rows) => `
      <tr${index === rows.length - 1 ? ' class="total"' : ''}>
        <td>${escapeHtml(row.label)}</td>
        <td class="right">${escapeHtml(row.amount)}</td>
      </tr>
    `).join('')

    return `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice ${escapeHtml(invoiceNumber)}</title>
          <style>
            @page { size: A4; margin: 0; }
            * { box-sizing: border-box; }
            html, body { min-height: 100%; }
            body { margin: 0; background: #f4f5f7; color: #1f2933; font-family: Arial, Helvetica, sans-serif; }
            .page { width: 210mm; min-height: 297mm; margin: 16px auto; background: #fff; padding: 23mm 14mm 18mm; display: flex; flex-direction: column; box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12); }
            h1, h2, h3, p { margin: 0; }
            .top { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
            .brand img { max-width: 150px; max-height: 92px; object-fit: contain; }
            .logo-fallback { width: 120px; height: 82px; border-radius: 18px; background: ${escapeHtml(companyProfile.primaryColor)}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 30px; font-weight: 700; }
            .company { text-align: right; color: #4b5563; font-size: 14px; line-height: 1.55; }
            .company strong { display: block; color: #111827; font-size: 15px; }
            .company .branch-name { color: #111827; font-weight: 700; }
            .title-row { display: grid; grid-template-columns: 1fr auto 1fr; gap: 20px; align-items: center; margin: 24px 0 28px; }
            .title-row::before, .title-row::after { content: ""; height: 1px; background: #d8dde3; }
            h1 { color: #111827; font-size: 16px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; }
            .billing { display: grid; grid-template-columns: 1.15fr 1fr; gap: 52px; margin-bottom: 28px; }
            .bill-to { color: #4b5563; font-size: 14px; line-height: 1.45; }
            .bill-to-title { color: #111827; font-weight: 500; margin-bottom: 4px; }
            .bill-to strong { display: block; color: #111827; font-size: 16px; margin-bottom: 6px; }
            .invoice-meta { width: 100%; border-collapse: collapse; font-size: 13px; }
            .invoice-meta td { padding: 5px 0; border: 0; }
            .invoice-meta td:first-child { color: #111827; font-weight: 700; }
            .invoice-meta td:last-child { color: #374151; text-align: left; padding-left: 30px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            .line-table th { background: ${tableHeaderColor}; color: #111827; font-weight: 700; padding: 10px 12px; text-align: left; border: 1px solid ${tableHeaderBorderColor}; }
            .line-table td { padding: 13px 12px; border: 1px solid #eceff3; vertical-align: top; line-height: 1.35; }
            .line-table .narrow { width: 40px; }
            .section-title { margin: 22px 0 10px; font-size: 13px; font-weight: 700; color: #111827; letter-spacing: 0.04em; text-transform: uppercase; }
            .right { text-align: right; }
            .center { text-align: center; }
            .summary-area { display: grid; grid-template-columns: 1fr 310px; gap: 28px; margin-top: 18mm; align-items: start; }
            .thanks { color: #4b5563; font-size: 12px; padding-top: 4px; }
            .totals { border-collapse: collapse; font-size: 13px; }
            .totals td { border: 0; padding: 8px 10px; }
            .totals tr.total td { border-top: 1px solid #d8dde3; border-bottom: 1px solid #d8dde3; font-weight: 700; padding-top: 12px; padding-bottom: 12px; }
            .balance { display: flex; justify-content: space-between; margin-top: 12px; border-top: 1px solid #d8dde3; border-bottom: 1px solid #d8dde3; padding: 13px 10px; font-size: 16px; font-weight: 700; }
            .payments th, .payments td { border: 1px solid #eceff3; padding: 7px 8px; }
            .payments th { background: #f8fafc; }
            .terms { margin-top: auto; padding-top: 36px; color: #4b5563; font-size: 12px; line-height: 1.45; }
            .terms strong { display: block; color: #111827; margin-bottom: 3px; }
            .footer { margin-top: 22px; border-top: 1px solid ${escapeHtml(companyProfile.primaryColor)}; padding-top: 8px; color: #64748b; font-size: 10px; text-align: center; }
            @media print {
              body { background: #fff; }
              .page { width: 210mm; min-height: 297mm; margin: 0; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <main class="page">
            <section class="top">
              <div class="brand">
                ${renderCompanyLogoHtml(companyProfile.logoUrl, companyProfile.name)}
              </div>
              <div class="company">
                <strong>${escapeHtml(companyProfile.name)}</strong>
                ${companyProfile.branchName ? `<div class="branch-name">${escapeHtml(companyProfile.branchName)}</div>` : ''}
                <div>${escapeHtml(companyProfile.address || 'Company address not set')}</div>
                ${companyContact ? `<div>${escapeHtml(companyContact)}</div>` : ''}
                ${companyProfile.registrationDetails ? `<div>${escapeHtml(companyProfile.registrationDetails)}</div>` : ''}
              </div>
            </section>

            <div class="title-row">
              <h1>Invoice</h1>
            </div>

            <section class="billing">
              <div class="bill-to">
                <div class="bill-to-title">Bill To</div>
                <strong>${escapeHtml(customerName)}</strong>
                <div>${escapeHtml(customerAddress || 'Address not set')}</div>
                <div>${escapeHtml(compactText(customerEmail, customerPhone))}</div>
              </div>
              <div>
                <table class="invoice-meta">
                  <tbody>
                    <tr><td>Invoice#</td><td>${escapeHtml(invoiceNumber)}</td></tr>
                    <tr><td>Invoice Date</td><td>${escapeHtml(formatInvoiceDate(order.order_time || order.created_on))}</td></tr>
                    <tr><td>Booking Ref</td><td>${escapeHtml(String(order.reference_id || order.id || '-'))}</td></tr>
                    <tr><td>Status</td><td>${escapeHtml(order.status_label || String(order.status ?? '-') )}</td></tr>
                    <tr><td>Payment</td><td>${escapeHtml(balanceDue <= 0.005 ? 'Paid' : 'Unpaid')}</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <p class="section-title">Particulars</p>
            <table class="line-table">
              <thead>
                <tr>
                  <th class="center narrow">#</th>
                  <th>Particulars</th>
                  <th>Description</th>
                  <th class="right">Qty</th>
                  <th class="right">Rate</th>
                  <th class="right">Amount</th>
                </tr>
              </thead>
              <tbody>${renderedParticulars}</tbody>
            </table>

            <section class="summary-area">
              <div class="thanks">Thanks for your booking.</div>
              <div>
                <table class="totals">
                  <tbody>
                    ${renderedTotals}
                    <tr><td>Paid</td><td class="right">${escapeHtml(formatMoney(paidAmount, currency))}</td></tr>
                  </tbody>
                </table>
                <div class="balance">
                  <span>Balance Due</span>
                  <span>${escapeHtml(formatMoney(balanceDue, currency))}</span>
                </div>
              </div>
            </section>

            <p class="section-title">Payments</p>
            ${payments.length > 0 ? `
              <table class="payments">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Status</th>
                    <th class="right">Amount</th>
                  </tr>
                </thead>
                <tbody>${renderedPayments}</tbody>
              </table>
            ` : `<p style="color:#64748b;font-size:12px;">No payment records recorded for this booking.</p>`}

            <section class="terms">
              <strong>Terms &amp; Conditions</strong>
              All services provided are subject to the terms and conditions outlined at the time of booking.
            </section>
            <section class="footer">
              ${escapeHtml(compactText(companyProfile.name, companyProfile.address, companyContact))}
            </section>
          </main>
        </body>
      </html>
    `
  }, [
    companyProfile,
    currency,
    formatPaymentChannelLabel,
    formatPaymentStatus,
    getPaymentDisplayReference,
    isSuccessfulPaymentStatus,
    order,
    payments,
  ])

  const printInvoice = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1000,height=800')
    if (!printWindow) {
      toast.error('Unable to open print window. Use Preview, then print from there.')
      return
    }
    printWindow.document.open()
    printWindow.document.write(invoiceHtml)
    printWindow.document.close()
    printWindow.focus()
    printWindow.setTimeout(() => printWindow.print(), 250)
  }

  const downloadInvoice = () => {
    const blob = new Blob([invoiceHtml], { type: 'text/html;charset=utf-8' })
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = `booking-invoice-${order.reference_id || order.id || 'download'}.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    toast.success('Invoice downloaded')
  }

  const printFromPreview = () => {
    const frameWindow = previewFrameRef.current?.contentWindow
    if (!frameWindow) {
      toast.error('Preview is still loading. Please try again.')
      return
    }
    frameWindow.focus()
    frameWindow.print()
  }

  const jobInvoicePath = linkedJob?.id != null
    ? withServiceInstanceId(
      `/finance/invoices/${linkedJob.id}/a4-preview`,
      String(linkedJob.serviceInstanceId ?? resolvedServiceInstanceId),
      // linkedJob.serviceInstanceId ?? resolvedServiceInstanceId,
    )
    : null

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ReceiptText className="h-4 w-4" />
              Invoice
            </CardTitle>
            <CardDescription>
              Download or print the booking invoice with particulars and payment history.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={downloadInvoice}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={printInvoice}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
            {jobInvoicePath ? (
              <Button asChild variant="secondary" size="sm">
                <Link to={jobInvoicePath}>
                  Job invoice
                  <ExternalLink className="ml-2 h-3.5 w-3.5" />
                </Link>
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Particulars: {(order.order_items ?? []).length} line{(order.order_items ?? []).length === 1 ? '' : 's'}
            {' · '}
            Payments: {payments.length} record{payments.length === 1 ? '' : 's'}
          </p>
          {linkedJob ? (
            <p>
              Linked job {linkedJob.jobRef ? `#${linkedJob.jobRef}` : `#${linkedJob.id}`} also has a job invoice with labour/expense particulars.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>Booking invoice preview</DialogTitle>
          </DialogHeader>
          <iframe
            ref={previewFrameRef}
            title="Booking invoice preview"
            srcDoc={invoiceHtml}
            className="h-[70vh] w-full border-0 bg-muted"
          />
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button type="button" variant="outline" onClick={downloadInvoice}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button type="button" onClick={printFromPreview}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
